import React, { useCallback, useEffect, useState } from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FadeIn from 'react-fade-in';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/Page';
import Breadcrumb from 'src/components/Breadcrumb';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  selectGlobalInvoicesKpis,
  selectGlobalInvoicesList,
  selectGlobalInvoicesListCount,
  selectGlobalInvoicesQueryOptions
} from 'src/store/selectors/globalInvoiceSelectors';
import {
  clearGlobalInvoices,
  downloadGlobalInvoicesListAction,
  LOAD_GLOBAL_INVOICES,
  loadGlobalInvoices
} from 'src/store/actions/globalInvoiceActions';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { GlobalInvoiceTab } from 'src/utils/constants/transactions';
import { toMexicoCityInstant } from 'src/utils/momentLocalTimezone';
import useDebounce from 'src/hooks/useDebounce';
import InvoiceDetailDrawer from 'src/components/AccountStatement/InvoiceDetailDrawer';
import Toolbar from './Toolbar';
import GlobalInvoiceTable from './GlobalInvoiceTable';
import useKpiStyles from './kpiStyles';

const GlobalInvoicesView = () => {
  const { classes } = useKpiStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const invoices = useSelector(selectGlobalInvoicesList);
  const count = useSelector(selectGlobalInvoicesListCount);
  const { totalCartera, collectedInRange, pending } = useSelector(
    selectGlobalInvoicesKpis
  );
  const queryOptions = useSelector(selectGlobalInvoicesQueryOptions);
  const loading = useSelector(isLoadingSelector([LOAD_GLOBAL_INVOICES.action]));

  const [search, setSearch] = useState('');
  const [concept, setConcept] = useState('');
  const [dateRange, setDateRange] = useState(() => ({
    afterDate: toMexicoCityInstant().startOf('month'),
    beforeDate: toMexicoCityInstant()
  }));
  const [tab, setTab] = useState(GlobalInvoiceTab.ALL);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);

  // dateRange is intentionally omitted from the deps array: this effect only
  // runs on mount to seed the initial (current-month) load, while later
  // dateRange changes are routed through applyFilters/debounceDispatch below.
  useEffect(() => {
    dispatch(
      loadGlobalInvoices({
        page: 0,
        afterDate: dateRange.afterDate,
        beforeDate: dateRange.beforeDate
      })
    );
    return () => dispatch(clearGlobalInvoices());
  }, [dispatch]);

  // Every filter change is routed through this single debounced dispatcher
  // (matching AccountStatementDetail.js's established convention), not just
  // search — lodash.debounce keeps one pending timer per debounced function,
  // so a later call (e.g. a concept change made right after typing) replaces
  // the earlier one instead of racing it. Splitting search onto its own
  // immediate-vs-debounced branch previously caused a stale debounced search
  // dispatch to fire after a newer filter change and silently revert it.
  const debounceDispatch = useDebounce(
    (options) => dispatch(loadGlobalInvoices(options)),
    400
  );

  const applyFilters = useCallback(
    (overrides) => {
      debounceDispatch({
        ...queryOptions,
        page: 0,
        search,
        category: concept || undefined,
        afterDate: dateRange.afterDate,
        beforeDate: dateRange.beforeDate,
        tab: tab === GlobalInvoiceTab.ALL ? undefined : tab,
        ...overrides
      });
    },
    [debounceDispatch, queryOptions, search, concept, dateRange, tab]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setSearch(value);
      applyFilters({ search: value });
    },
    [applyFilters]
  );

  const handleConceptChange = useCallback(
    (value) => {
      setConcept(value);
      applyFilters({ category: value || undefined });
    },
    [applyFilters]
  );

  const handleDateRangeChange = useCallback(
    (value) => {
      setDateRange(value);
      applyFilters({
        afterDate: value.afterDate,
        beforeDate: value.beforeDate
      });
    },
    [applyFilters]
  );

  const handleTabChange = useCallback(
    (value) => {
      setTab(value);
      applyFilters({ tab: value === GlobalInvoiceTab.ALL ? undefined : value });
    },
    [applyFilters]
  );

  const handleClear = useCallback(() => {
    setSearch('');
    setConcept('');
    setDateRange({});
    setTab(GlobalInvoiceTab.ALL);
    // SAVE_GLOBAL_INVOICES_OPTIONS merges into existing state.options, so every
    // filter key must be explicitly set to undefined here — omitting a key
    // would leave its previous (stale) value in the store. Routed through the
    // same debounced dispatcher as every other filter change so it supersedes
    // (rather than races) a pending debounced call from a recent keystroke.
    debounceDispatch({
      page: 0,
      search: undefined,
      category: undefined,
      paymentMethod: undefined,
      afterDate: undefined,
      beforeDate: undefined,
      tab: undefined
    });
  }, [debounceDispatch]);

  const handleChangePage = useCallback(
    (_, value) => {
      dispatch(loadGlobalInvoices({ page: value }));
    },
    [dispatch]
  );

  const handleDownload = useCallback(() => {
    dispatch(downloadGlobalInvoicesListAction());
  }, [dispatch]);

  const handleViewInvoice = useCallback((invoiceId) => {
    setViewingInvoiceId(invoiceId);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setViewingInvoiceId(null);
  }, []);

  const handleDrawerMutated = useCallback(() => {
    dispatch(loadGlobalInvoices(queryOptions));
  }, [dispatch, queryOptions]);

  return (
    <Page className={classes.root} title={t('NAVIGATION.GLOBAL_INVOICES')}>
      <FadeIn transitionDuration={200}>
        <Container maxWidth={false}>
          <Box className={classes.headerRow}>
            <Box>
              <Breadcrumb />
              <Typography
                style={{ fontWeight: '400', marginTop: 4 }}
                color="primary"
                variant="h1"
              >
                {t('NAVIGATION.GLOBAL_INVOICES')}
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box className={classes.cardsRow}>
                <Box className={classes.card}>
                  <Box className={classes.carteraIcon}>
                    <MoneyIcon fill="#28A745" />
                  </Box>
                  <Box>
                    <Typography
                      style={{ marginBottom: 8 }}
                      component="p"
                      variant="body2"
                    >
                      {t('TRANSACTION.TOTAL_CARTERA')}
                    </Typography>
                    <Typography color="primary" component="h2" variant="h2">
                      {formatAmountWithDecimals(totalCartera)}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.card}>
                  <Box className={classes.collectedIcon}>
                    <CheckCircleIcon />
                  </Box>
                  <Box>
                    <Typography
                      style={{ marginBottom: 8 }}
                      component="p"
                      variant="body2"
                    >
                      {t('TRANSACTION.COLLECTED_IN_RANGE')}
                    </Typography>
                    <Typography color="primary" component="h2" variant="h2">
                      {formatAmountWithDecimals(collectedInRange)}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.card}>
                  <Box className={classes.pendingIcon}>!</Box>
                  <Box>
                    <Typography
                      style={{ marginBottom: 8 }}
                      component="p"
                      variant="body2"
                    >
                      {t('TRANSACTION.PENDING_AMOUNT')}
                    </Typography>
                    <Typography color="primary" component="h2" variant="h2">
                      {formatAmountWithDecimals(pending)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Toolbar
                search={search}
                onSearchChange={handleSearchChange}
                concept={concept}
                onConceptChange={handleConceptChange}
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                tab={tab}
                onTabChange={handleTabChange}
                onClear={handleClear}
                onDownload={handleDownload}
              />
              <GlobalInvoiceTable
                count={count}
                page={queryOptions.page}
                loading={loading}
                handleChangePage={handleChangePage}
                transactions={invoices}
                onView={handleViewInvoice}
              />
            </Grid>
          </Grid>
        </Container>
      </FadeIn>
      <InvoiceDetailDrawer
        open={Boolean(viewingInvoiceId)}
        invoiceId={viewingInvoiceId}
        onClose={handleCloseDrawer}
        onMutated={handleDrawerMutated}
      />
    </Page>
  );
};

export default GlobalInvoicesView;
